"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { twMerge } from "tailwind-merge";

import { Oriented, orientedLinkMap } from "@/data/constants/oriented";
import { links } from "@/data/links";
import { RelatedSymbol } from "@/features/analysis/related-symbol/services/related-symbols";
import { useLang } from "@/hooks/use-lang";
import { useUpdateQuery } from "@/hooks/use-update-query";

import { Button } from "ui/core/button";
import { scoreBadgeColors } from "ui/widget/badge/dataset";

import { DesktopFollowButton } from "../../components/follow-button/DesktopFollowButton";
import { MobileFollowButton } from "../../components/follow-button/MobileFollowButton";

type RelatedSymbolListProps = {
  data: RelatedSymbol[];
  oriented: Oriented;
};

export const RelatedSymbolList = ({
  data,
  oriented,
}: RelatedSymbolListProps) => {
  const { lang, t } = useLang();
  const { updateQuery } = useUpdateQuery();
  const searchParams = useSearchParams();
  const viewAll = searchParams.get("relevance") === "all";

  const displayData = viewAll ? data : data.slice(0, 5);
  const getLink = orientedLinkMap.get(oriented) ?? links.myAnalysisOverview;
  return (
    <>
      <ul
        role="list"
        className="divide-y divide-zinc-300"
        aria-label="related symbols"
      >
        {displayData.map((item) => (
          <Link
            href={`/${lang}${getLink({ symbol: item.symbol ?? "" })}`}
            key={item.symbol}
            className="flex items-center justify-between gap-x-6 px-3 py-5 hover:bg-neutral-50"
          >
            <div className="flex min-w-0 items-center gap-x-4">
              <ScoreBadge score={item[oriented] ?? 0} />
              <div className="min-w-0 flex-auto">
                <p className="font-medium text-zinc-900">{item.symbol}</p>
                <p className="truncate text-sm text-zinc-500">
                  {item.companyName}
                </p>
              </div>
            </div>
            <MobileFollowButton
              symbol={item.symbol ?? ""}
              className="block @3xl:hidden"
            />
            <DesktopFollowButton
              symbol={item.symbol ?? ""}
              className="hidden @3xl:flex"
            />
          </Link>
        ))}
      </ul>
      <Button
        variant="secondary"
        className="mx-auto block w-full max-w-[325px] self-center"
        onClick={() => {
          updateQuery({
            name: "relevance",
            value: viewAll ? "less" : "all",
          });
        }}
      >
        {viewAll
          ? t.analysis.relatedSymbol.viewLess
          : t.analysis.relatedSymbol.viewAll}
      </Button>
    </>
  );
};

const ScoreBadge = ({ score }: { score: number }) => {
  const basicStyles = scoreBadgeColors[score]
    ? scoreBadgeColors[score]
    : scoreBadgeColors[0];
  return (
    <span
      className={twMerge(
        "h-[45px] w-[45px] rounded-full border text-center text-lg font-bold leading-[45px]",
        basicStyles
      )}
    >
      {score}
    </span>
  );
};
